import React, { useCallback, useContext, useMemo } from "react";
import { batchDeleteCategory, searchCategory } from "apis";
import { ButtonLink, DataTable, Text, Toast } from "components/commons";
import { VenueContext } from "contexts";
import { useApi, useFilter, useModal, useMount, useRouter, useSelectItems } from "hooks";
import { categoryListFilterRequest, categoryListResponse } from "mappers";
import { mapObject, pluralize } from "services";
import columns from "./columns";
import { HeaderA } from "components/headers";
import lang from "translations";
import { Path } from "paths";
import { DeleteModal } from "components/modals";
import { ModuleWrapper } from "components/fragments";
import { mixpanel, TrackEvent } from "mixpanel";

const CategoryList = () => {
  const { venue } = useContext(VenueContext);
  const { history } = useRouter();

  const deleteModal = useModal();

  const {
    request: searchCategoryRequest,
    loading: loadingCategory,
    result: searchCategoryResult = { data: [], metadata: { total: 0 } },
    error,
    mappedData,
  } = useApi({
    api: searchCategory,
    mapper: categoryListResponse,
    isArray: true,
    handleOwnError: true,
  });

  const { request: batchDeleteCategoryRequest, loading: deletingCategory } = useApi({
    api: batchDeleteCategory,
  });

  const { modifyFilters, filterState, requestState } = useFilter({
    venueId: venue.venueId,
    page: 1,
    pageSize: 20,
  });

  useMount(() => {
    fetchCategories(requestState);
    mixpanel.track(TrackEvent.VisitedPage, {
      Page: lang.categoryList,
    });
  });

  const renderCategoryName = useCallback(
    (name, id) =>
      id === 0 ? (
        <Text italic>{name}</Text>
      ) : (
        // <ButtonLink className="text-left" onClick={() => history.push(Path.CATEGORY_ID(id))}>
        <ButtonLink className="text-left" path={Path.CATEGORY_ID(id)}>
          {name}
        </ButtonLink>
      ),
    []
  );

  const renderDescription = useCallback(
    (description, id) => <Text italic={id === 0}>{description}</Text>,
    []
  );

  const renderProductCount = useCallback(
    (productCount, id) =>
      productCount > 0 ? (
        <ButtonLink
          className="text-left"
          onClick={() => {
            history.push(Path.PRODUCT, { categories: [{ value: id }] });
          }}
          // path={(Path.PRODUCT, { categories: [{ value: id }] })}
        >
          {productCount}
        </ButtonLink>
      ) : (
        <Text>{productCount}</Text>
      ),
    [history]
  );

  const prepareCategoryList = useCallback(() => {
    return mappedData.map((category) => {
      const { id, name, description, productCount } = category;
      return {
        id,
        name: renderCategoryName(name, id),
        description: renderDescription(description, id),
        productCount: renderProductCount(productCount, id),
      };
    });
  }, [mappedData, renderCategoryName, renderProductCount, renderDescription]);

  const categories = useMemo(() => {
    return prepareCategoryList();
  }, [prepareCategoryList]);

  const { selected, selectedCount, setSelected, isAllSelected, setSelectAll, clearSelected } =
    useSelectItems({
      items: categories,
      indeterminate: true,
    });

  const fetchCategories = useCallback(
    (requestState) => {
      searchCategoryRequest(mapObject(requestState, categoryListFilterRequest));
    },
    [searchCategoryRequest]
  );

  const toBeDeletedCategory = useMemo(() => {
    if (selectedCount === 1) {
      const category = mappedData.find((d) => d.id === parseInt(Object.keys(selected)[0]));
      return category.name;
    }
    return null;
  }, [selectedCount, mappedData, selected]);

  const batchDeleteCategoryCb = useCallback(async () => {
    mixpanel.track(TrackEvent.ClickedButton, {
      Button: lang.deleteCategory,
      Page: lang.categoryList,
    });

    await batchDeleteCategoryRequest({
      categoryIds: Object.keys(selected).map((id) => parseInt(id)),
    });
    Toast({
      content:
        selectedCount > 1
          ? lang.populate(lang.categoriesDeleted, [selectedCount])
          : lang.populate(lang.categoryDeleted, [toBeDeletedCategory]),
      success: true,
      icon: "check",
    }).open();
    deleteModal.close();
    const { requestState } = modifyFilters({ page: 1, pageSize: filterState.pageSize });
    fetchCategories(requestState);
    clearSelected();
  }, [
    batchDeleteCategoryRequest,
    deleteModal,
    selected,
    selectedCount,
    fetchCategories,
    toBeDeletedCategory,
    filterState.pageSize,
    modifyFilters,
    clearSelected,
  ]);

  const changePageConfigCb = useCallback(
    (pageProps) => {
      clearSelected();
      return modifyFilters(pageProps);
    },
    [modifyFilters, clearSelected]
  );

  const actionSelectContent = useMemo(() => {
    if (selectedCount > 0) {
      return {
        text: `${selectedCount} ${pluralize(selectedCount, "item", "items")}`,
        options: [
          {
            text: selectedCount > 1 ? lang.deleteSelectedCategories : lang.deleteSelectedCategory,
            value: "0",
            onClick: () => {
              deleteModal.show({
                ok: batchDeleteCategoryCb,
              });
            },
          },
        ],
      };
    }
  }, [batchDeleteCategoryCb, deleteModal, selectedCount]);

  return (
    <ModuleWrapper>
      <HeaderA
        title={lang.categories}
        description={lang.createCategoriesToLink}
        button={{
          path: Path.CATEGORY_CREATE,
          text: lang.createNewCategory,
        }}
        className="mb-md"
      />
      <DataTable
        actionSelectContent={actionSelectContent}
        page={filterState.page}
        pageSize={filterState.pageSize}
        onChangePage={changePageConfigCb}
        fetchList={fetchCategories}
        total={searchCategoryResult.metadata.total}
        loading={loadingCategory}
        columns={columns}
        data={categories}
        error={error}
        selected={selected}
        setSelected={setSelected}
        isAllSelected={isAllSelected}
        setSelectAll={setSelectAll}
        disabled={[0]}
        minWidth="200px"
      />
      <DeleteModal
        deleting={deletingCategory}
        count={selectedCount}
        subject={selectedCount > 1 ? lang.categories.toLowerCase() : lang.category}
        subjectName={toBeDeletedCategory}
        {...deleteModal}
      />
    </ModuleWrapper>
  );
};

export default CategoryList;
